// Diagram content for timeline steps, with node ids that stay the same across the whole history.
import { emptyModel, groupChanges, type ChangeGroup } from '@modelviewer/core';
import { buildGraph, type Graph, type GraphOptions } from '../diagram/graph';
import type { LayoutInput } from '../diagram/useLayout';
import { buildDiffGraph } from '../diff/diffGraph';
import type { Timeline } from './model';

/** The step whose model is shown for `index`: itself, or the last earlier step with a known model. */
export function shownStep(timeline: Timeline, index: number): number {
  let i = index;
  while (i >= 0 && !timeline.steps[i]!.model) i--;
  return i;
}

export function idsAt(timeline: Timeline, index: number | null): (key: string) => string {
  const ids = index === null ? undefined : timeline.ids[index];
  return (key) => ids?.get(key) ?? key;
}

/** Key of the entity with diagram id `id` at a step. */
export function keyAt(timeline: Timeline, index: number, id: string): string | undefined {
  for (const [key, value] of timeline.ids[index] ?? []) if (value === id) return key;
  return undefined;
}

/** The diagram at a step, colored with its changes when the step has its own model. */
export function stepGraph(timeline: Timeline, index: number, options: GraphOptions): Graph | null {
  const shown = shownStep(timeline, index);
  if (shown < 0) return null;
  const step = timeline.steps[shown]!;
  const idAfter = idsAt(timeline, shown);
  if (shown !== index || !step.diff) {
    const graph = buildGraph(step.model!, options);
    for (const n of graph.nodes) n.id = idAfter(n.key);
    for (const e of graph.edges) {
      e.source = idAfter(e.source);
      e.target = idAfter(e.target);
    }
    return graph;
  }
  const before = step.previous === null ? emptyModel() : timeline.steps[step.previous]!.model!;
  return buildDiffGraph({ before, after: step.model!, diff: step.diff, options, idAfter, idBefore: idsAt(timeline, step.previous) });
}

const nextFrame = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

/**
 * One layout for the whole history: every entity that ever existed, at its largest size (ghost rows
 * included), with every edge. Stepping through the timeline then only shows, hides and recolors cards.
 */
export async function unionLayoutInput(timeline: Timeline, options: GraphOptions, isCancelled: () => boolean): Promise<LayoutInput | null> {
  const sizes = new Map<string, { width: number; height: number; namespace: string | null; schema: string | null }>();
  const edges = new Map<string, LayoutInput['edges'][number]>();
  for (let i = 0; i < timeline.steps.length; i++) {
    if (!timeline.steps[i]!.model) continue;
    const graph = stepGraph(timeline, i, options);
    for (const n of graph?.nodes ?? []) {
      const size = sizes.get(n.id);
      if (!size) sizes.set(n.id, { width: n.width, height: n.height, namespace: n.namespace, schema: n.schema });
      else {
        size.width = Math.max(size.width, n.width);
        size.height = Math.max(size.height, n.height);
        // Grouped layouts use where the entity ended up.
        size.namespace = n.namespace;
        size.schema = n.schema;
      }
    }
    for (const e of graph?.edges ?? []) {
      if (e.source === e.target) continue;
      const id = `${e.target}->${e.source}`;
      if (!edges.has(id)) edges.set(id, { id, from: e.target, to: e.source });
    }
    if (i % 10 === 9) {
      await nextFrame();
      if (isCancelled()) return null;
    }
  }
  return { nodes: [...sizes].map(([id, s]) => ({ id, ...s })), edges: [...edges.values()] };
}

export interface EntityEvent {
  index: number;
  group: ChangeGroup;
}

/** The steps that touched the entity with diagram id `id`, oldest first. */
export function entityEvents(timeline: Timeline, id: string): EntityEvent[] {
  const events: EntityEvent[] = [];
  timeline.steps.forEach((step, index) => {
    if (!step.model || !step.diff) return;
    const now = keyAt(timeline, index, id);
    const before = step.previous === null ? undefined : keyAt(timeline, step.previous, id);
    if (now === undefined && before === undefined) return;
    const beforeModel = step.previous === null ? emptyModel() : timeline.steps[step.previous]!.model!;
    const groups = groupChanges(step.diff.changes, step.model, beforeModel);
    const group = now !== undefined ? groups.find((g) => g.key === now && g.state !== 'removed') : groups.find((g) => g.key === before && g.state === 'removed');
    if (group) events.push({ index, group });
  });
  return events;
}
